import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { maintenanceService, MaintenanceConfig } from '../services/maintenanceService';

interface MaintenanceContextType {
    isMaintenance: boolean;
    maintenanceConfig: MaintenanceConfig | null;
    isLoading: boolean;
    timeUntilNextCheck: number; // Milliseconds until next check
    refresh: () => Promise<void>;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

/**
 * Provider for maintenance mode context
 * Checks external maintenance.json file on startup and every 30 seconds
 */
export const MaintenanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isMaintenance, setIsMaintenance] = useState<boolean>(false);
    const [maintenanceConfig, setMaintenanceConfig] = useState<MaintenanceConfig | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [timeUntilNextCheck, setTimeUntilNextCheck] = useState<number>(0);
    const lastCheckTimeRef = React.useRef<number>(0);

    const checkMaintenance = async () => {
        try {
            // Use a timeout to ensure we don't wait too long
            // For initial check, use shorter timeout (3 seconds) to fail fast
            const config = await Promise.race([
                maintenanceService.checkMaintenanceMode(),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)) // 3 second timeout for initial check
            ]);
            
            setMaintenanceConfig(config);
            const isActive = maintenanceService.isMaintenanceActive(config);
            setIsMaintenance(isActive);
            lastCheckTimeRef.current = Date.now();
        } catch (error) {
            // On error, assume not in maintenance mode
            // This ensures the app continues to work even if maintenance check fails
            // (e.g., network issues, blob storage unavailable, etc.)
            console.debug('[MaintenanceProvider] Error checking maintenance mode, assuming disabled:', error);
            setIsMaintenance(false);
            setMaintenanceConfig(null);
            lastCheckTimeRef.current = Date.now();
        } finally {
            // CRITICAL: Set loading to false AFTER state updates to ensure React batches them
            // This prevents providers from mounting before maintenance check completes
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const CHECK_INTERVAL = 30000; // 30 seconds
        let checkInterval: NodeJS.Timeout;
        let countdownInterval: NodeJS.Timeout;
        let initialTimeout: NodeJS.Timeout;
        let isMounted = true;

        // Initial check - wait for it to complete before allowing app to proceed
        // But don't block forever - timeout after 5 seconds as fallback
        initialTimeout = setTimeout(() => {
            if (isMounted) {
                console.warn('[MaintenanceProvider] Maintenance check timed out after 5 seconds, proceeding with app');
                setIsLoading(false);
            }
        }, 5000);

        // Perform initial check and wait for it to complete
        // This MUST complete before providers mount
        checkMaintenance().finally(() => {
            if (isMounted) {
                clearTimeout(initialTimeout);
            }
        });

        // Set up periodic checking every 30 seconds
        checkInterval = setInterval(() => {
            maintenanceService.clearCache();
            checkMaintenance();
        }, CHECK_INTERVAL);

        // Set up countdown timer for next check (updates every second)
        countdownInterval = setInterval(() => {
            // Calculate time until next check
            const now = Date.now();
            const timeSinceLastCheck = now - lastCheckTimeRef.current;
            const timeUntilNext = Math.max(0, CHECK_INTERVAL - timeSinceLastCheck);
            setTimeUntilNextCheck(timeUntilNext);
        }, 1000);

        return () => {
            isMounted = false;
            clearTimeout(initialTimeout);
            clearInterval(checkInterval);
            clearInterval(countdownInterval);
        };
    }, []);

    const refresh = async () => {
        maintenanceService.clearCache();
        await checkMaintenance();
    };

    return (
        <MaintenanceContext.Provider value={{
            isMaintenance,
            maintenanceConfig,
            isLoading,
            timeUntilNextCheck,
            refresh
        }}>
            {children}
        </MaintenanceContext.Provider>
    );
};

/**
 * Hook to access maintenance context
 */
export const useMaintenance = (): MaintenanceContextType => {
    const context = useContext(MaintenanceContext);
    if (context === undefined) {
        throw new Error('useMaintenance must be used within a MaintenanceProvider');
    }
    return context;
};

